import { Link } from 'react-router-dom';

import { Route42InlineMarkdown } from '../../../components/ui/Route42Markdown';
import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';

import type { GroundedAnswerDto } from '../types/conversation';
import { deriveEvidenceStrength } from '../types/conversation';
import type { MachineDocument } from '../../documents/types/document';

export function EvidencePanel({
  machineId,
  answer,
  documents,
  onClose,
}: {
  machineId: string;
  answer: GroundedAnswerDto;
  documents: MachineDocument[];
  onClose: () => void;
}) {
  const strength = deriveEvidenceStrength(answer);
  const documentById = new Map(documents.map((document) => [document.id, document]));

  return (
    <aside className="r42-evidence-panel" aria-label="Answer evidence">
      <header className="r42-evidence-panel__head">
        <div><span className="r42-eyebrow">Evidence</span><h2>Why {ROUTE42_ASSISTANT_NAME} answered this way</h2></div>
        <button className="r42-icon-button" onClick={onClose} aria-label="Close evidence"><i className="bi bi-x-lg" aria-hidden="true" /></button>
      </header>
      <div className="r42-evidence-panel__body">
        <div className={`r42-evidence-strength r42-evidence-strength--${strength.toLocaleLowerCase()}`}>
          <span>Evidence strength</span><strong>{humanize(strength)}</strong>
        </div>
        {answer.conflicting_evidence ? (
          <div className="r42-evidence-conflict"><i className="bi bi-exclamation-triangle" aria-hidden="true" /><span>{ROUTE42_ASSISTANT_NAME} found conflicting evidence. The answer preserves that uncertainty rather than hiding it.</span></div>
        ) : null}
        <div className="r42-evidence-list">
          {(answer.citations ?? []).map((citation) => {
            const document = citation.document_id ? documentById.get(citation.document_id) : undefined;
            return (
              <article className="r42-evidence-card" key={`${citation.citation_id}-${citation.artifact_id}`}>
                <div className="r42-evidence-card__top"><span>{citation.citation_id}</span><small>{humanize(citation.artifact_type)}</small></div>
                <strong>{citation.label || document?.filename || humanize(citation.artifact_type)}</strong>
                {document ? <span className="r42-evidence-source">Source: {document.filename}</span> : null}
                {citation.excerpt ? <blockquote>{citation.excerpt}</blockquote> : null}
                {locatorText(citation.source_locator) ? <p>{locatorText(citation.source_locator)}</p> : null}
                {document ? <Link to={`/machines/${machineId}/documents/${document.id}`}>Open document <i className="bi bi-arrow-up-right" aria-hidden="true" /></Link> : null}
              </article>
            );
          })}
        </div>
        {answer.uncertainty ? <div className="r42-evidence-uncertainty"><span className="r42-eyebrow">Uncertainty</span><p><Route42InlineMarkdown content={answer.uncertainty} /></p></div> : null}
      </div>
    </aside>
  );
}

function humanize(value: string): string {
  return value.toLocaleLowerCase().replaceAll('_', ' ').replace(/^./, (char) => char.toLocaleUpperCase());
}

function locatorText(locator?: Record<string, unknown> | null): string {
  if (!locator) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(locator)) {
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'object') continue;
    parts.push(`${humanize(key)}: ${String(value)}`);
  }
  return parts.slice(0, 3).join(' • ');
}
